if (!customElements.get("quick-cart-drawer")) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", this.init.bind(this));
        this.parentElement.addEventListener("shopify:section:select", () => this.open());
        this.parentElement.addEventListener("shopify:section:deselect", () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains("is--open")) {
        document
          .querySelector("body")
          .classList.remove("overflow-hidden");
      }

      this.querySelector(".button--close").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.querySelector(
        ".quick-cart-drawer__backdrop"
      ).addEventListener("click", this.close.bind(this));

      this.querySelector(".quick-cart-drawer__blocks").addEventListener("keydown", e => {
        if (e.key === "Escape" && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement =
            focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === "Tab";

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    /**
     * [Fix 2026-10-09] Quick Shop buttons previously only got their click
     * listener from a MutationObserver, which fires on *later* DOM changes, never
     * for buttons already on the page. If nothing else added/removed elements in
     * <body> after this script loaded, the buttons stayed unbound and clicking
     * Quick Shop did nothing. One delegated listener on the document covers
     * buttons already on the page and any added later (load more, recommendations,
     * section re-renders). Bound once, since init() is called again by
     * card-product-slider.js and on section reloads in the theme editor.
     */
    initTriggers() {
      if (window.__quickCartTriggersBound) return;
      window.__quickCartTriggersBound = true;

      document.addEventListener("click", event => {
        const trigger = event.target.closest(".quick-cart-drawer__trigger");
        if (!trigger || trigger.disabled || trigger.classList.contains("is--loading")) return;
        const drawer = document.querySelector("quick-cart-drawer");
        if (!drawer) return;
        event.preventDefault();
        drawer.fetchProductForQuickCartDrawer(event, trigger);
      });
    }

    async fetchProductForQuickCartDrawer(event, clickedTrigger) {
      /** stop autoplay on quick cart trigger */
      const cardSlider = event.target.closest("card-product-slider");
      if (cardSlider) {
        cardSlider.slider?.autoplay?.stop();
        cardSlider.classList.add("product--open-on-quick-cart");
      }

      const triggerButton = clickedTrigger || event.target.closest(".quick-cart-drawer__trigger");
      const isRecommendations = triggerButton
        ? triggerButton.classList.contains("quick-cart-drawer__trigger--recommendations")
        : event.target.classList.contains("quick-cart-drawer__trigger--recommendations");

      const productCard = event.target.closest("product-card");
      let productOptions = [];

      if (!isRecommendations) {
        if (!productCard) return;
        // get all checked options
        productOptions = productCard.querySelectorAll(
          'input[type="radio"]:checked, select'
        );
      }

      const trigger = event.target.closest("[data-product-url]");
      if (!trigger) return;
      trigger.classList.add("is--loading");

      let loaded = false;
      try {
        const productCardResponse = await fetch(
          `${trigger.dataset.productUrl}/?view=quick-cart`
        );
        if (!productCardResponse || !productCardResponse.ok) {
          throw new Error(`Quick Shop request failed (${productCardResponse && productCardResponse.status})`);
        }

        const productCardHTML = await productCardResponse.text();
        const fetchedWrapper = document.createElement("DIV");
        fetchedWrapper.insertAdjacentHTML("beforeend", productCardHTML);
        const fetchedProduct = fetchedWrapper.querySelector(".quick-cart-product");
        if (!fetchedProduct) {
          throw new Error("Quick Shop response did not contain .quick-cart-product");
        }
        this.querySelector(".quick-cart-drawer__main").innerHTML = "";
        this.querySelector(".quick-cart-drawer__main").append(fetchedProduct);
        loaded = true;

        if (typeof Swiper !== "undefined") {
          this.sliderInstance = new Swiper(".quick-cart-drawer__media-swiper", {
            slidesPerView: 1.25,
            spaceBetween: 8,
            freeMode: {
              enabled: true
            },
            breakpoints: {
              750: {
                slidesPerView: 2
              }
            }
          });
        }
      } catch (error) {
        console.error("[Quick Shop]", error);
      } finally {
        trigger.classList.remove("is--loading");
      }

      // If the product could not be loaded, send the shopper to the product page
      // instead of opening an empty drawer (or crashing on missing elements).
      if (!loaded) {
        if (trigger.dataset.productUrl) {
          window.location.href = trigger.dataset.productUrl;
        }
        return;
      }

      const drawerProductCard = this.querySelector("product-card");

      if (!isRecommendations && drawerProductCard) {
        // update quick cart drawer options with product options
        productOptions.forEach(productOption => {
          const quickCartDrawerOption = this.querySelector(
            `[name="${productOption.name}"][value="${productOption.value}"]`
          );
          const quickCartDrawerOptionLabel =
            quickCartDrawerOption?.parentElement?.parentElement?.querySelector('legend');

          // before updating radio button state, remove checked attribute for specific name
          this.querySelectorAll(
            `[name="${productOption.name}"]`
          ).forEach(radioInput => {
            radioInput.removeAttribute("checked");
            radioInput.closest("li")?.classList.remove("checked");
          });

          if (quickCartDrawerOption) {
            quickCartDrawerOption.setAttribute("checked", "");
            const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');
            if (quickCartDrawerOptionLabelSpan) {
              quickCartDrawerOptionLabelSpan.innerHTML = productOption.value;
            }
          }

          // update variant id in product card with selected variant id
          const drawerIdInput = drawerProductCard.querySelector('input[name="id"]');
          const cardIdInput = productCard.querySelector('input[name="id"]');
          if (drawerIdInput && cardIdInput) {
            drawerIdInput.value = cardIdInput.value;
          }

          // update radio button state
          if (quickCartDrawerOption) {
            quickCartDrawerOption
              .closest("li")
              ?.classList.add("checked");
          }

          // initilise for disabled states to be checked and updated
          drawerProductCard.init();
        });
      }

      // short wait before opening drawer
      setTimeout(() => {
        this.open();
      }, 500);

      // update label when variant is selected
      this.querySelector(".quick-cart-drawer__main")
        .querySelectorAll(".variant-option-radio-input")
        .forEach(radioInput => {
          radioInput.addEventListener("change", event => {
            const quickCartDrawerOptionLabel = event.target.parentElement.parentElement.querySelector('legend');
            const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');
            if (quickCartDrawerOptionLabelSpan) {
              quickCartDrawerOptionLabelSpan.innerHTML = event.target.value;
            }

            // update variant id in product card with selected variant id
            const selectedVariantId = this.querySelector(
              ".product-card__add-to-cart--form input[name='id']"
            )?.value;

            // get product variants data
            const productVariantsData = this.querySelector("product-card")?.variantsObj || [];
            const featuredMedia = productVariantsData.find(
              variant => variant.id == selectedVariantId
            )?.featured_media;

            if (featuredMedia) {
              this.setActiveMedia(featuredMedia.id);
            }
          });
        });
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(
        this.querySelectorAll("[data-media-id]")
      ).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound && this.sliderInstance) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer core functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector("body").classList.add("overflow-hidden");
      const quickCartDrawer = document.querySelector(".quick-cart-drawer__blocks");
      const closeButton = quickCartDrawer.querySelector(".button--close");
      quickCartDrawer.setAttribute("tabindex", "0");
      closeButton.setAttribute("tabindex", "0");
      this.classList.add("is--open");
      this.opened();

      const firstFocusableElement = this.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector("shop-the-look-drawer");

      if(shopTheLookDrawer && !shopTheLookDrawer.classList.contains("is--open")) {
        document
          .querySelector("body")
          .classList.remove("overflow-hidden");
      }
      if(!shopTheLookDrawer) {
        document
          .querySelector("body")
          .classList.remove("overflow-hidden");
      }
      this.classList.remove("is--open");
      this.closed();
      this.toggleAriaExpanded();

      /** start autoplay on quick cart drawer close */
      const openSlider = document.querySelector("card-product-slider.product--open-on-quick-cart");
      if (openSlider) {
        openSlider.slider?.autoplay?.start();
        openSlider.classList.remove("product--open-on-quick-cart");
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest("button"))
          event.target
            .closest("button")
            .setAttribute("aria-expanded", true);
        this.querySelector(".button--close").setAttribute(
          "aria-expanded",
          true
        );
      } else {
        document
          .querySelectorAll('[aria-controls="quick-cart-drawer"]')
          .forEach(button => {
            button.setAttribute("aria-expanded", false);
          });
      }
    }

    opened() {
      const openedEvent = new Event("opened", { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event("closed", { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define("quick-cart-drawer", QuickCartDrawer);
}
